import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS } from "@tCentro/theme/design";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    description = "description",
    linkTitle = "linkTitle",
    linkURL = "linkURL",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.linkTitle]: string;
        [fields.linkURL]: string;
    };
}

export interface WhyCentroTitleDescStyles {
    containerWrapper?: InjectableCss;
    title?: InjectableCss;
    description?: InjectableCss;
    readMoreLink?: InjectableCss;
}

export const whyCentroTitleDescStyles: WhyCentroTitleDescStyles = {
    containerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            flex-direction: column;
            text-align: center;
        `,
    },
    title: {
        css: css`
            margin-bottom: 40px;
            color: ${getProp("theme.typography.h2.color")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-weight: 600;
                            font-size: 24px;
                            line-height: 32px;
                            margin-bottom: 24px;
                        `,
                        css`
                            font-weight: 600;
                            font-size: 24px;
                            line-height: 32px;
                            margin-bottom: 24px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    description: {
        css: css`
            margin-top: 0 !important;
            margin-bottom: 40px !important;
            font-weight: 400;
            color: ${getProp("theme.typography.p.color")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 16px;
                            line-height: 20px;
                            margin-bottom: 24px;
                            margin-bottom: 56px !important;
                            display: flex;
                            flex-direction: column;
                        `,
                        css`
                            font-size: 16px;
                            line-height: 20px;
                            margin-bottom: 24px;
                            margin-bottom: 56px !important;
                            display: flex;
                            flex-direction: column;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    readMoreLink: {
        css: css`
            color: ${getColor("text.link")};
            font-weight: 400;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-weight: 600;
                            font-size: 18px;
                            line-height: 24px;
                            margin-top: 24px;
                            justify-content: center;
                        `,
                        css`
                            font-weight: 600;
                            font-size: 18px;
                            line-height: 24px;
                            margin-top: 24px;
                            justify-content: center;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
};

const styles = whyCentroTitleDescStyles;
export const VideoWidget: FC<OwnProps> = ({ fields }) => {
    return (
        <StyledWrapper className="why-centro-intro" {...styles.containerWrapper}>
            {fields.title && (
                <Typography variant="h2" {...styles.title}>
                    {translate("{0}", fields.title)}
                </Typography>
            )}
            {fields.description && (
                <Typography variant="h5" {...styles.description}>
                    {translate("{0}", fields.description)}
                    <Link href={fields.linkURL} {...styles.readMoreLink}>
                        {parse(transformContentHtml(fields.linkTitle), parserOptions)}
                    </Link>
                </Typography>
            )}
        </StyledWrapper>
    );
};

const whyCentroTitleIntroWidget: WidgetModule = {
    component: VideoWidget,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                name: fields.title,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                displayName: "Title",
            },
            {
                fieldType: "Translatable",
                name: fields.description,
                editorTemplate: "TextField",
                displayName: "Introduction",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.linkTitle,
                editorTemplate: "TextField",
                displayName: "Link title",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.linkURL,
                editorTemplate: "TextField",
                displayName: "Link URL",
                defaultValue: "",
            },
        ],
    },
};

export default whyCentroTitleIntroWidget;
